import { useState, useEffect } from "react";
import { Pill } from "./Pill";
import { SHARED_LABELS, ui } from "@/strings";
import PiExclamationMarkStroke from "@markeroo/icons/src/stroke/alerts/PiExclamationMarkStroke";

export type AssignmentStatusType = "draft" | "queued" | "processing" | "completed" | "failed";
export type AssessmentStatusType = "assessed" | "assessing" | "needsAssessment" | "assessmentFailed";
export type ClassStatusType = "archived";

export type StatusType = AssignmentStatusType | AssessmentStatusType | ClassStatusType;

export interface StreamingProgress {
  wordsGenerated: number;
  stage: string | null;
  isConnected: boolean;
}

interface StatusBadgeProps {
  status: StatusType;
  issueCount?: number | null;
  streamingProgress?: StreamingProgress | null;
}

const STATUS_CONFIG: Record<
  StatusType,
  {
    label: string;
    variant: "success" | "warning" | "error" | "info" | "processing";
    icon: string;
  }
> = {
  // Assignment statuses
  draft: {
    label: SHARED_LABELS.STATUS_DRAFT,
    variant: "info",
    icon: "○",
  },
  queued: {
    label: SHARED_LABELS.STATUS_PROCESSING,
    variant: "processing",
    icon: "⋯",
  },
  processing: {
    label: SHARED_LABELS.STATUS_PROCESSING,
    variant: "processing",
    icon: "⋯",
  },
  completed: {
    label: SHARED_LABELS.STATUS_COMPLETED,
    variant: "success",
    icon: "✓",
  },
  failed: {
    label: SHARED_LABELS.STATUS_FAILED,
    variant: "error",
    icon: "✕",
  },
  // Assessment statuses
  assessed: {
    label: SHARED_LABELS.ASSESSED,
    variant: "success",
    icon: "✓",
  },
  assessing: {
    label: SHARED_LABELS.ASSESSING,
    variant: "processing",
    icon: "⋯",
  },
  needsAssessment: {
    label: SHARED_LABELS.NEEDS_HIGHLIGHTING,
    variant: "warning",
    icon: "!",
  },
  assessmentFailed: {
    label: SHARED_LABELS.STATUS_FAILED,
    variant: "error",
    icon: "✕",
  },
  // Class status
  archived: {
    label: SHARED_LABELS.ARCHIVED,
    variant: "info",
    icon: "",
  },
} as const;

const MESSAGE_ROTATION_INTERVAL_MS = 3000;

/**
 * Format word count for display - abbreviate large numbers (e.g., "1.2K")
 */
function formatWordCount(words: number): string {
  if (words >= 1000) {
    const k = words / 1000;
    return k >= 10 ? `${Math.round(k)}K` : `${k.toFixed(1)}K`;
  }
  return words.toLocaleString();
}

export function StatusBadge({ status, issueCount, streamingProgress }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const isProcessing = status === "processing" || status === "queued" || status === "assessing";

  // Message rotation for processing states (fallback when no streaming data)
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    // Always rotate messages while processing, regardless of streaming state
    if (!isProcessing) {
      return;
    }

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ui.loading.processingMessages.length);
    }, MESSAGE_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isProcessing]);

  // For assessed documents, show issue count if available
  if (status === "assessed" && issueCount !== undefined && issueCount !== null) {
    if (issueCount === 0) {
      return (
        <Pill variant="success" icon="✓">
          {SHARED_LABELS.NO_ISSUES}
        </Pill>
      );
    }
    return (
      <Pill variant="info" icon={String(issueCount)}>
        {issueCount === 1 ? SHARED_LABELS.ISSUE : SHARED_LABELS.ISSUES}
      </Pill>
    );
  }

  // For processing states, show rotating messages with integrated word count
  if (isProcessing) {
    const showWordCount = streamingProgress?.isConnected && streamingProgress.wordsGenerated > 0;
    return (
      <Pill variant={config.variant}>
        <span className="flex items-center gap-2">
          <span className="transition-opacity duration-300">
            {ui.loading.processingMessages[messageIndex]}
          </span>
          {showWordCount && (
            <>
              <span className="opacity-50">{ui.loading.bulletSeparator}</span>
              <span className="opacity-70 tabular-nums">
                {ui.loading.wordsCount(formatWordCount(streamingProgress.wordsGenerated))}
              </span>
            </>
          )}
        </span>
      </Pill>
    );
  }

  // Use Pikaicons exclamation for needsAssessment
  const icon = status === "needsAssessment"
    ? <PiExclamationMarkStroke size={16} />
    : config.icon || undefined;

  return (
    <Pill variant={config.variant} icon={icon}>
      {config.label}
    </Pill>
  );
}
